// A row of the turn order and a token on the board finding each other by name (qa-runs#1226; design/20 §3.2). ONE rule,
// read by the server when something arrives and by the DM's tracker for what it offers: names go by `naming.ts`'s
// numbering, what players see by `combatantSeen`, the test their strip uses.
//
// The table only ever acts on an arrival, so a link the DM took off stays off (`clearLinks` is told what arrived; it
// never sweeps). What links with no press is one clear answer that changes nothing a player sees; anything less is an
// offer on the row (`linkOffers`). Neither leaves the DM's screen: the server's reading goes to whoever runs the order.
import { combatantOf, combatantSeen } from './encounter.js';
import { splitNumbered } from './naming.js';
const folded = (name) => name.trim().toLowerCase();
const named = (name) => { const s = splitNumbered(name); return { stem: folded(s.stem), n: s.n }; };
const sameName = (a, b) => folded(a) === folded(b);
const playersToken = (o) => o.pc || o.owners.length > 0;
/**
 * The tokens to tell someone about: plain ones (no link, no entry, no companion) a player owns while a character of
 * theirs at the table has no token. `isDm` every player's, read from `owners`; otherwise only the viewer's own, read
 * from `mine`. It reads only what this viewer was already sent.
 */
export function unlinkedTokens(objects, members, meId, isDm) {
    const tokens = Object.values(objects).filter((o) => o.kind === 'token');
    const out = [];
    for (const o of tokens) {
        if (o.character || o.entry || o.companion)
            continue;
        const owner = isDm ? members.find((m) => m.role === 'player' && o.owners?.includes(m.id)) : o.mine === true ? members.find((m) => m.id === meId) : undefined;
        if (!owner)
            continue;
        const free = owner.characters.filter((c) => !tokens.some((t) => t.character === c.id));
        if (free.length)
            out.push({ token: o.id, name: o.name ?? 'A token', owner: owner.id, characters: free.map(({ id, name }) => ({ id, name })) });
    }
    return out;
}
/** Who the strip is drawn for: everyone at the table who isn't the DM, or a player with nothing when there is nobody yet. */
export function stripViewers(members) {
    const others = members.filter((m) => m.role !== 'dm').map((m) => ({ id: m.id, role: (m.role === 'player' ? 'player' : 'watcher') }));
    return others.length ? others : [{ id: '', role: 'player' }];
}
/** The rows with no token on the board. One whose token was deleted is free too: taking a new token drops the old. */
export const freeRows = (enc, objects) => enc.combatants.filter((c) => !c.tokens.some((id) => objects[id]));
/** The tokens acting for no row: no prop, and no trap (`markers`, the segments of the families placed as markers). */
export function freeTokens(enc, objects, markers = []) {
    return Object.values(objects).filter((o) => o.kind === 'token' && o.layer !== 'scenery' && !(o.entry && markers.includes(o.entry.segment)) && !combatantOf(enc, o.id));
}
/** What linking `token` to the free row `row` would change on the strip, for any viewer. */
export function sightChange(row, token, state, viewers) {
    let change = null;
    const after = { ...row, tokens: [token] };
    for (const v of viewers) {
        const before = combatantSeen(row, state, v);
        const then = combatantSeen(after, state, v);
        if (before && !then)
            return 'hides';
        if (!before && then)
            change = 'shows';
    }
    return change;
}
/** A row and a token that could be each other with no doubt about the name: the full name, or a row with no number and the stem. */
const couldBe = (c, o) => {
    const row = named(c.name);
    return sameName(c.name, o.name) || (row.n === null && row.stem === named(o.name).stem);
};
/**
 * The links made with no press, for what just arrived (`rows` or `tokens`): one clear answer on both sides, a player's
 * token only by its full name, and the strip the same for everyone after as before.
 */
export function clearLinks(enc, state, viewers, arrival, markers = []) {
    const rows = freeRows(enc, state.objects);
    const tokens = freeTokens(enc, state.objects, markers);
    const out = [];
    for (const c of rows) {
        if (arrival.rows && !arrival.rows.includes(c.id))
            continue;
        const answers = tokens.filter((o) => couldBe(c, o));
        if (answers.length !== 1)
            continue;
        const o = answers[0];
        if (arrival.tokens && !arrival.tokens.includes(o.id))
            continue;
        if (rows.filter((x) => couldBe(x, o)).length !== 1)
            continue;
        if (playersToken(o) && !sameName(c.name, o.name))
            continue;
        if (sightChange(c, o.id, state, viewers))
            continue;
        out.push({ row: c.id, token: o.id });
    }
    return out;
}
/**
 * What each free row may be offered: free tokens of its name ignoring the number, a player's among them. Within a name
 * the same full names pair first, then lowest number to lowest number; what is left over has no pair.
 */
export function linkOffers(enc, state, viewers, markers = []) {
    const byNumber = (list) => list.map((x) => ({ x, n: named(x.name).n ?? 0 })).sort((a, b) => a.n - b.n).map((y) => y.x);
    const tokens = freeTokens(enc, state.objects, markers);
    const stems = new Map();
    for (const c of freeRows(enc, state.objects)) {
        const stem = named(c.name).stem;
        const group = stems.get(stem);
        if (group)
            group.rows.push(c);
        else
            stems.set(stem, { rows: [c], tokens: tokens.filter((o) => named(o.name).stem === stem) });
    }
    const out = { pairs: [], rows: 0, tokens: 0 };
    for (const group of stems.values()) {
        if (!group.tokens.length)
            continue;
        out.rows += group.rows.length;
        out.tokens += group.tokens.length;
        const pair = (c, o) => out.pairs.push({ row: c.id, token: o.id, change: sightChange(c, o.id, state, viewers) });
        const left = [...group.tokens];
        const rest = group.rows.filter((c) => {
            const i = left.findIndex((o) => sameName(c.name, o.name));
            if (i < 0)
                return true;
            pair(c, left.splice(i, 1)[0]);
            return false;
        });
        const rows = byNumber(rest);
        const others = byNumber(left);
        for (let i = 0; i < Math.min(rows.length, others.length); i += 1)
            pair(rows[i], others[i]);
    }
    return out;
}
/**
 * What a link brings the row (design/20 §3.2): stamped by the server on `encounter.link`, whoever made it. With one token
 * on the board the row takes its name; a bonus still at 0 takes the token's; in a running fight a player's token on a
 * row with no roll asks them to roll (qa-runs 1192). A link that only takes tokens off brings nothing.
 */
export function linkTakes(enc, objects, link) {
    const c = enc.combatants.find((x) => x.id === link.id);
    if (!c)
        return {};
    const gained = link.tokens.filter((t) => !c.tokens.includes(t) && objects[t]).map((t) => objects[t]);
    if (!gained.length)
        return {};
    const present = link.tokens.filter((t) => objects[t]);
    const name = present.length === 1 ? objects[present[0]].name : null;
    const bonus = gained.map((o) => o.values.initBonus).find((b) => b);
    const asks = enc.running && c.initRoll === null && gained.some((o) => o.owners.length > 0) && !(enc.asked ?? []).includes(c.id);
    return { ...(name && name !== c.name ? { name } : {}), ...(c.initBonus === 0 && bonus ? { initBonus: bonus } : {}), ...(asks ? { ask: true } : {}) };
}
/** "Goblin 1 linked to its row." / "Goblin 1, Goblin 2 and Goblin 3 linked to their rows." / "… and 2 others …" */
export function linkedNotice(names) {
    if (names.length === 1)
        return `${names[0]} linked to its row.`;
    const list = names.length === 2 ? `${names[0]} and ${names[1]}`
        : names.length === 3 ? `${names[0]}, ${names[1]} and ${names[2]}`
            : `${names[0]}, ${names[1]} and ${names.length - 2} others`;
    return `${list} linked to their rows.`;
}
/** What the DM is told when a player's token acts for a row (issue 1022). */
export const playerMoveNote = (token, row) => `${token} belongs to a player: they can move it on ${row}'s turn.`;
export const LINK_HIDES = "Linking hides this from the players' strip";
export const LINK_SHOWS = 'Linking shows this to the players';
//# sourceMappingURL=tokenMatch.js.map